import React, { useState, useRef } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import DateInput from '@/components/ui/DateInput';
import { Label } from '@/components/ui/label';
import { CheckboxRow } from '@/components/ui/checkbox-row';
import { X, CheckCircle2, ChevronRight, Loader2, Camera, Plus } from 'lucide-react';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { ViewType, User } from '../types';
import { compressImageToJpeg } from '@/lib/compress-image';
import { formatAmount } from '@/lib/utils';
import { useToast } from '../hooks/use-toast';

export default function AddExpense({ store, setView }: { store: any, setView: (v: ViewType) => void }) {
  const { users, currentUser, addExpense, updateExpense, editingExpense, setEditingExpense } = store;
  const { showToast } = useToast();
  
  const isEditing = !!editingExpense;

  const [title, setTitle] = useState(editingExpense?.title || '');
  // 使用本地日期 (YYYY-MM-DD)，避免 toISOString() 的 UTC 偏移
  const formatLocalDate = (d: Date) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${dd}`;
  };
  const [expenseDate, setExpenseDate] = useState(() => {
    if (editingExpense?.expenseDate) {
      return String(editingExpense.expenseDate).split('T')[0];
    }
    return formatLocalDate(new Date());
  });
  const [payerId, setPayerId] = useState(editingExpense?.payerId || currentUser?.id || '');
  const [splitMethod, setSplitMethod] = useState<'equal' | 'itemized'>(
    editingExpense?.items && editingExpense.items.length > 0 ? 'itemized' : 'equal'
  );
  
  // Equal Split State
  const [amount, setAmount] = useState(editingExpense && (!editingExpense.items || editingExpense.items.length === 0) ? editingExpense.amount.toString() : '');
  const [selectedUsers, setSelectedUsers] = useState<string[]>(() => {
    if (editingExpense?.participants && Array.isArray(editingExpense.participants) && editingExpense.participants.length > 0) {
      return editingExpense.participants.map((p: any) => p.userId);
    }
    return users?.map((u: User) => u.id) || [];
  });

  // Itemized List State
  const [items, setItems] = useState<{ id: string, name: string, amount: string, debtorId: string }[]>(
    editingExpense?.items && editingExpense.items.length > 0 
      ? editingExpense.items.map((item: any) => ({
          id: item.id,
          name: item.name,
          amount: item.amount.toString(),
          debtorId:
            item.debtorId ||
            editingExpense.participants.find((p: any) => p.userId !== editingExpense.payerId)?.userId ||
            users?.[0]?.id ||
            ''
        }))
      : [{ id: '1', name: '', amount: '', debtorId: users?.find((u: User) => u.id !== currentUser?.id)?.id || users?.[0]?.id || '' }]
  );
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>(editingExpense?.imageUrl || '');
  const [debtorId, setDebtorId] = useState<string>(() => {
    if (editingExpense?.items && editingExpense.items.length > 0 && editingExpense.participants && Array.isArray(editingExpense.participants) && editingExpense.participants.length > 0) {
      return editingExpense.participants.find((p: any) => p.userId !== editingExpense.payerId)?.userId || editingExpense.participants[0]?.userId;
    }
    return users?.find((u: User) => u.id !== currentUser?.id)?.id || users?.[0]?.id || '';
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<{ title?: boolean; amount?: boolean; users?: boolean }>({});

  // Refs for scroll-to-validation
  const titleRef = useRef<HTMLInputElement>(null);
  const amountSectionRef = useRef<HTMLDivElement>(null);
  const usersSectionRef = useRef<HTMLDivElement>(null);

  // Derived Values
  const totalAmount = splitMethod === 'equal' 
    ? parseFloat(amount) || 0 
    : items.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);

  const calculateParticipantData = () => {
    if (splitMethod === 'equal') {
      const perPerson = Math.ceil(selectedUsers.length > 0 ? totalAmount / selectedUsers.length : 0);
      return selectedUsers.map(uid => ({ userId: uid, amountDue: perPerson }));
    } else {
      const amountMap = new Map<string, number>();
      items
        .filter(item => item.amount !== '')
        .forEach((item) => {
          const due = parseFloat(item.amount) || 0;
          const targetDebtorId = item.debtorId || debtorId;
          amountMap.set(targetDebtorId, (amountMap.get(targetDebtorId) || 0) + due);
        });
      return Array.from(amountMap.entries()).map(([userId, amountDue]) => ({ userId, amountDue }));
    }
  };

  const handleUserToggle = (userId: string) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId) 
        : [...prev, userId]
    );
  };

  const addItemRow = () => {
    setItems(prev => {
      const inheritedDebtorId = prev[prev.length - 1]?.debtorId || debtorId;
      return [...prev, { id: String(Date.now()), name: '', amount: '', debtorId: inheritedDebtorId }];
    });
  };

  const updateItem = (index: number, field: 'name' | 'amount' | 'debtorId', value: string) => {
    const newItems = [...items];
    newItems[index][field] = value;
    
    // Auto-generate next row if the current one has an amount and it's the last row
    if (field === 'amount' && value !== '' && index === items.length - 1) {
      newItems.push({
        id: String(Date.now()),
        name: '',
        amount: '',
        debtorId: newItems[index].debtorId || debtorId
      });
    }
    
    setItems(newItems);
  };

  const removeItem = (id: string) => {
    if (items.length > 1) {
      setItems(prev => prev.filter(item => item.id !== id));
    } else {
      setItems([{ id: '1', name: '', amount: '', debtorId }]);
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // 展示上傳中狀態的占位預覽
    const tempUrl = URL.createObjectURL(file);
    setImagePreview(tempUrl);

    try {
      // 前端壓縮：轉換 JPEG 並限制 1 MB
      const compressed = await compressImageToJpeg(file, 409_600);
      URL.revokeObjectURL(tempUrl);
      setImageFile(compressed);
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result as string);
      reader.readAsDataURL(compressed);
    } catch (err) {
      console.error('圖片壓縮失敗，改用原檔：', err);
      URL.revokeObjectURL(tempUrl);
      // 壓縮失敗時，若原檔超過 1 MB 則拒絕上傳
      if (file.size > 1_048_576) {
        showToast('圖片壓縮失敗且檔案超過 1MB，請選擇較小的圖片', 'error');
        setImageFile(null);
        setImagePreview(editingExpense?.imageUrl || '');
        return;
      }
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result as string);
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async () => {
    // Validate required fields
    const newErrors: { title?: boolean; amount?: boolean; users?: boolean } = {};
    if (!title) newErrors.title = true;
    if (totalAmount === 0) newErrors.amount = true;
    if (splitMethod === 'equal' && selectedUsers.length === 0) newErrors.users = true;

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      // Scroll to first error
      if (newErrors.title) {
        titleRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        titleRef.current?.focus();
      } else if (newErrors.amount) {
        amountSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } else if (newErrors.users) {
        usersSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }
    setErrors({});
    
    setIsSubmitting(true);
    const participantData = calculateParticipantData();

    const formData = new FormData();
    formData.append('title', title);
    formData.append('amount', totalAmount.toString());
    formData.append('payerId', payerId);
    // 傳送純日期字串 (YYYY-MM-DD)，後端與 DB 存成 DATE-like 字串以避免時區誤差
    formData.append('expenseDate', expenseDate);
    formData.append('participants', JSON.stringify(participantData));
    formData.append(
      'items',
      JSON.stringify(
        splitMethod === 'itemized'
          ? items
              .filter(i => i.amount !== '')
              .map(i => ({ ...i, debtorId: i.debtorId || debtorId }))
          : []
      )
    );
    if (imageFile) {
      formData.append('image', imageFile);
    }

    try {
      if (isEditing) {
        await updateExpense(editingExpense.id, formData);
      } else {
        await addExpense(formData);
      }
      handleCancel();
    } catch (error) {
       console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setEditingExpense(null);
    setView(isEditing ? 'expenses' : 'dashboard');
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-8 md:mb-10 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-black tracking-tight mb-2">{isEditing ? '修改支出紀錄' : '建立新支出'}</h1>
          <p className="text-xs md:text-base text-muted-foreground font-medium">支援均分與「明細記帳」模式，自動加總明細金額。</p>
        </div>
        <Button variant="ghost" onClick={handleCancel} className="text-primary font-bold hover:bg-primary/10 rounded-xl px-4 h-12 self-end sm:self-auto">
          <X size={20} className="mr-2" />
          取消
        </Button>
      </div>

      <div className="space-y-6 md:space-y-8">
        {/* Basic Info */}
        <Card className="bg-muted/30 border-border/50 rounded-2xl md:rounded-[2.5rem] p-6 md:p-8 shadow-wash overflow-hidden">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="md:col-span-2 space-y-4">
              <Label className="text-[10px] font-black text-muted-foreground uppercase tracking-[0.2em] ml-1">支出名稱與日期</Label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <Input 
                  ref={titleRef}
                  value={title}
                  onChange={(e) => { setTitle(e.target.value); if (errors.title) setErrors(p => ({ ...p, title: false })); }}
                  className={`sm:col-span-2 bg-background border-none rounded-2xl h-12 md:h-14 text-base md:text-lg font-bold shadow-sm transition-all ${errors.title ? 'ring-2 ring-destructive' : ''}`}
                  placeholder="全聯採買、晚餐分帳..." 
                />
                <DateInput
                  value={expenseDate}
                  onChange={(e) => setExpenseDate(e.target.value)}
                  wrapperClassName=""
                  className="bg-background border-none rounded-l-2xl h-12 md:h-14 font-bold shadow-sm"
                />
              </div>
            </div>

            <div className="space-y-4">
               <Label className="text-[10px] font-black text-muted-foreground uppercase tracking-[0.2em] ml-1">由誰付款？</Label>
               <div className="relative">
                  <select 
                    value={payerId}
                    onChange={(e) => setPayerId(e.target.value)}
                    className="w-full bg-background border-none rounded-2xl h-12 md:h-14 px-6 font-black appearance-none cursor-pointer focus:ring-1 focus:ring-primary shadow-sm text-sm"
                  >
                    {users.map((u: User) => (
                      <option key={u.id} value={u.id}>{u.nickname} {u.id === currentUser?.id ? '(本人)' : ''}</option>
                    ))}
                  </select>
                  <div className="absolute right-6 top-1/2 -translate-y-1/2 pointer-events-none opacity-40">
                    <ChevronRight size={18} className="rotate-90" />
                  </div>
               </div>
            </div>
          </div>
        </Card>

        {/* Image Upload */}
        <Card className="bg-muted/10 border-border/50 rounded-2xl md:rounded-[2.5rem] p-6 lg:p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
              <Camera size={20} />
            </div>
            <h3 className="text-xl font-black text-foreground uppercase tracking-tight">收據圖片 (選填)</h3>
          </div>
          
          <div className={`relative border-2 border-dashed rounded-[2rem] transition-all flex flex-col items-center justify-center min-h-[200px] overflow-hidden ${imagePreview ? 'border-primary' : 'border-border hover:border-primary/50 bg-muted/20'}`}>
             {imagePreview ? (
               <div className="relative w-full h-full min-h-[200px] group">
                 <img src={imagePreview} alt="Receipt Preview" className="w-full h-full object-cover max-h-[400px]" />
                 <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <button 
                      onClick={() => {
                        setImageFile(null);
                        setImagePreview('');
                      }}
                      className="p-4 bg-destructive text-white rounded-full hover:scale-110 transition-transform shadow-xl"
                    >
                      <X size={24} />
                    </button>
                 </div>
               </div>
             ) : (
               <label className="w-full h-full absolute inset-0 cursor-pointer flex flex-col items-center justify-center p-8 text-center gap-4">
                 <div className="w-16 h-16 rounded-2xl bg-primary/10 text-primary flex items-center justify-center group-hover:scale-110 transition-transform">
                   <Plus size={32} />
                 </div>
                 <div>
                  <p className="text-base font-black text-foreground">上傳收據或照片</p>
                  <p className="text-[10px] text-muted-foreground font-medium uppercase tracking-[0.2em] mt-1">選填，建議大小 2MB 以內</p>
                 </div>
                 <input type="file" accept="image/*" className="hidden" onChange={handleImageUpload} />
               </label>
             )}
          </div>
        </Card>

        {/* Split Method Toggle */}
        <div className="flex bg-muted/40 p-1.5 rounded-2xl gap-2 w-full max-w-md mx-auto border border-border/20 shadow-inner">
          <button 
            onClick={() => setSplitMethod('equal')}
            className={`flex-1 py-2 md:py-3 text-xs md:text-sm font-black rounded-xl transition-all ${splitMethod === 'equal' ? 'bg-background shadow-md text-primary translate-y-[-1px]' : 'text-muted-foreground hover:bg-muted/60'}`}
          >
            平均分攤
          </button>
          <button 
            onClick={() => setSplitMethod('itemized')}
            className={`flex-1 py-2 md:py-3 text-xs md:text-sm font-black rounded-xl transition-all ${splitMethod === 'itemized' ? 'bg-background shadow-md text-primary translate-y-[-1px]' : 'text-muted-foreground hover:bg-muted/60'}`}
          >
            明細記帳
          </button>
        </div>

        {/* Dynamic Section Based on Split Method */}
        {splitMethod === 'equal' ? (
          <div className="space-y-6 md:space-y-8">
            <Card ref={amountSectionRef} className={`bg-primary/5 rounded-2xl md:rounded-[2.5rem] p-6 md:p-8 text-center border-2 border-dashed transition-all ${errors.amount ? 'border-destructive ring-2 ring-destructive/30' : 'border-primary/20'}`}>
              <span className="text-[10px] font-black text-primary uppercase tracking-[0.3em] mb-2 block">輸入總支出金額</span>
              <div className="relative max-w-xs mx-auto">
                <span className="absolute left-0 top-1/2 -translate-y-1/2 text-2xl md:text-4xl font-black text-primary/40">$</span>
                <Input 
                  type="number"
                  inputMode="decimal"
                  value={amount}
                  onChange={(e) => { setAmount(e.target.value); if (errors.amount) setErrors(p => ({ ...p, amount: false })); }}
                  className="bg-transparent border-none text-center text-4xl md:text-6xl font-black text-primary h-16 md:h-24 focus-visible:ring-0 p-0"
                  placeholder="0"
                />
              </div>
              <p className="text-xs font-bold text-muted-foreground mt-2 md:mt-4">預計每人分擔 ${formatAmount(totalAmount / (selectedUsers.length || 1))}</p>
            </Card>

            <Card ref={usersSectionRef} className={`bg-muted/10 border-border/50 rounded-2xl md:rounded-[2.5rem] p-6 md:p-8 transition-all ${errors.users ? 'ring-2 ring-destructive/40' : ''}`}>
              <h3 className="text-lg md:text-xl font-bold text-primary mb-6 flex items-center justify-between">
                分帳參與成員
                <Badge variant="outline" className={`font-black ${errors.users ? 'border-destructive text-destructive' : 'border-primary/30 text-primary'}`}>{selectedUsers.length} 人</Badge>
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                {users.map((user: User) => {
                  const isSelected = selectedUsers.includes(user.id);
                  return (
                    <CheckboxRow
                      key={user.id}
                      checked={isSelected}
                      onCheckedChange={() => handleUserToggle(user.id)}
                      checkboxPosition="end"
                      className={`justify-between p-3 md:p-4 rounded-2xl border ${
                        isSelected ? 'bg-background border-primary shadow-sm' : 'bg-muted/20 border-border/10 opacity-60'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <Avatar className="h-8 w-8 rounded-lg overflow-hidden border border-border/30">
                          <AvatarFallback className="font-bold text-xs">{user.nickname[0]}</AvatarFallback>
                        </Avatar>
                        <span className="font-bold text-xs md:text-sm">{user.nickname}</span>
                      </div>
                    </CheckboxRow>
                  );
                })}
              </div>
            </Card>
          </div>
        ) : (
          <div className="space-y-6 md:space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-300">
            <Card ref={amountSectionRef} className={`bg-muted/10 border-border/50 rounded-2xl md:rounded-[2.5rem] p-6 md:p-8 transition-all ${errors.amount ? 'ring-2 ring-destructive/40' : ''}`}>
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-8">
                 <h3 className="text-lg md:text-xl font-bold text-primary">依明細加總記帳</h3>
                 <div className="flex items-center gap-3 bg-background/50 p-2 rounded-2xl border border-border/40 w-full sm:w-auto">
                   <Label className="text-[10px] font-black text-muted-foreground uppercase ml-2 whitespace-nowrap">請款對象</Label>
                   <select 
                      value={debtorId}
                      onChange={(e) => setDebtorId(e.target.value)}
                      className="bg-transparent border-none font-bold text-xs md:text-sm focus:ring-0 cursor-pointer min-w-[120px] flex-1"
                    >
                      {users.map((u: User) => (
                        <option key={u.id} value={u.id}>{u.nickname} {u.id === currentUser?.id ? '(本人)' : ''}</option>
                      ))}
                    </select>
                 </div>
              </div>

              <div className="space-y-3">
                {items.map((item, index) => (
                  <div key={item.id} className="md:grid md:grid-cols-12 md:gap-3 group animate-in zoom-in-95 duration-200 bg-background/40 md:bg-transparent rounded-xl p-3 md:p-0 border border-border/40 md:border-0">
                    <div className="md:col-span-4 flex items-center gap-2 md:gap-3 mb-2 md:mb-0">
                      <div className="hidden sm:flex w-8 h-8 rounded-lg bg-muted text-muted-foreground items-center justify-center font-bold text-xs shrink-0">
                        {index + 1}
                      </div>
                      <Input 
                        value={item.name}
                        onChange={(e) => updateItem(index, 'name', e.target.value)}
                        className="bg-background border-none rounded-xl h-10 md:h-12 font-bold shadow-sm text-sm md:text-base"
                        placeholder="品項名稱"
                      />
                    </div>
                    <div className="grid grid-cols-[1fr_1fr_auto] gap-2 md:contents">
                      <div className="md:col-span-3 relative">
                         <span className="absolute left-2 md:left-3 top-1/2 -translate-y-1/2 text-muted-foreground font-black text-xs">$</span>
                         <Input 
                          type="number"
                          inputMode="decimal"
                          value={item.amount}
                          onChange={(e) => updateItem(index, 'amount', e.target.value)}
                          className="bg-background border-none rounded-xl h-10 md:h-12 pl-5 md:pl-6 font-black text-right shadow-sm focus:ring-1 focus:ring-primary/40 transition-all text-sm md:text-base"
                          placeholder="0"
                        />
                      </div>
                      <div className="md:col-span-4">
                        <select
                          value={item.debtorId || debtorId}
                          onChange={(e) => updateItem(index, 'debtorId', e.target.value)}
                          className="w-full bg-background border-none rounded-xl h-10 md:h-12 px-2 md:px-3 font-bold text-xs shadow-sm"
                        >
                          {users.map((u: User) => (
                            <option key={u.id} value={u.id}>
                              {u.nickname} {u.id === currentUser?.id ? '(本人)' : ''}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="md:col-span-1 flex items-center justify-center">
                         <Button 
                           variant="ghost" 
                           size="icon" 
                           className="text-muted-foreground hover:text-destructive md:opacity-0 group-hover:opacity-100 transition-opacity h-10 w-10 md:h-8 md:w-8"
                           onClick={() => removeItem(item.id)}
                           aria-label="刪除此列"
                         >
                           <X size={14} />
                         </Button>
                      </div>
                    </div>
                  </div>
                ))}
                <Button 
                  variant="ghost" 
                  onClick={addItemRow}
                  className="w-full h-10 md:h-12 rounded-xl border border-dashed border-border/50 text-xs md:text-sm text-muted-foreground font-bold hover:bg-muted/50 mt-2"
                >
                  + 新增一列
                </Button>
              </div>

              <div className="mt-8 p-4 md:p-6 bg-primary/5 rounded-2xl border-2 border-primary/20">
                <div>
                  <span className="text-[10px] font-black text-primary uppercase tracking-widest block mb-1">各項累計總額</span>
                  <div className="text-2xl md:text-4xl font-black text-primary">${formatAmount(totalAmount)}</div>
                </div>
              </div>
            </Card>
          </div>
        )}

        <Button 
          onClick={handleSubmit}
          disabled={isSubmitting || totalAmount === 0}
          className="w-full py-6 md:py-10 bg-gradient-to-br from-primary to-accent text-primary-foreground rounded-2xl md:rounded-[2rem] font-black text-lg md:text-2xl shadow-xl hover:shadow-2xl active:scale-[0.98] transition-all flex items-center justify-center gap-3 disabled:opacity-50 disabled:grayscale"
        >
          {isSubmitting ? (
            <Loader2 className="animate-spin" size={24} />
          ) : (
            <>
              <CheckCircle2 size={24} className="md:w-8 md:h-8" />
              {isEditing ? '確認修改內容' : '確認建立帳目'}
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
